import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ReorderSettings({ open, onOpenChange }) {
  const [settings, setSettings] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [globals, setGlobals] = useState({ safety_days: "", target_days: "" });
  const [catInputs, setCatInputs] = useState({}); // {cat: {safety, target}}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [r, rr] = await Promise.all([
      api.get("/reorder/settings"),
      api.get("/reorder/readiness"),
    ]);
    setSettings(r.data); setReadiness(rr.data);
    setGlobals({
      safety_days: r.data.defaults.safety_days ?? "",
      target_days: r.data.defaults.target_days ?? "",
    });
    const inputs = {};
    for (const c of r.data.categories || []) {
      inputs[c.category] = {
        safety: c.safety_days_configured ?? "",
        target: c.target_days_configured ?? "",
      };
    }
    setCatInputs(inputs);
  };
  useEffect(() => { if (open) load(); }, [open]);

  const saveGlobals = async () => {
    setSaving(true);
    try {
      await api.put("/reorder/defaults", {
        safety_days: globals.safety_days === "" ? null : Number(globals.safety_days),
        target_days: globals.target_days === "" ? null : Number(globals.target_days),
      });
      toast.success("Default globali salvati"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const saveCategory = async (cat) => {
    const v = catInputs[cat] || {};
    try {
      await api.put(`/reorder/categories/${encodeURIComponent(cat)}`, {
        safety_days: v.safety === "" ? null : Number(v.safety),
        target_days: v.target === "" ? null : Number(v.target),
      });
      toast.success(`${cat} salvata`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const resetCategory = async (cat) => {
    try {
      await api.delete(`/reorder/categories/${encodeURIComponent(cat)}`);
      toast.success(`${cat} ripristinata ai default`); load();
    } catch { toast.error("Errore"); }
  };

  // Preview
  const preview = useMemo(() => {
    const avg = 5, unit = "unità";
    const safety = Number(globals.safety_days) || 0;
    const target = Number(globals.target_days) || 0;
    const lead = 2; // esempio
    return {
      avg, unit, safety, target, lead,
      reorder_point: avg * (lead + safety),
      target_stock: avg * target,
    };
  }, [globals]);

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="reorder-settings-dialog">
        <DialogHeader><DialogTitle className="font-serif">Impostazioni Riordino</DialogTitle></DialogHeader>
        {!settings || !readiness ? (
          <div className="p-6 text-center text-neutral-500">Caricamento…</div>
        ) : (
        <div className="space-y-6">
          {/* Readiness */}
          <div className="border border-[#E2D9C8] rounded-lg p-4 bg-[#FDFBF7]" data-testid="readiness-panel">
            <div className="label-eyebrow mb-2">Prontezza Smart Reorder</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><b>{readiness.suppliers.with_lead_time}</b>/{readiness.suppliers.total} <div className="text-xs text-neutral-500">fornitori con lead time</div></div>
              <div><b>{readiness.inventory.with_valid_unit}</b>/{readiness.inventory.total} <div className="text-xs text-neutral-500">prodotti con unità</div></div>
              <div><b>{readiness.recipe_mapping.products_in_a_recipe}</b>/{readiness.recipe_mapping.total} <div className="text-xs text-neutral-500">prodotti in ricette</div></div>
              <div><b>{readiness.consumption_history.distinct_days_with_consumption_last_30}</b>/{readiness.consumption_history.target_days} <div className="text-xs text-neutral-500">giorni con consumo (30g · target 21)</div></div>
              <div><b>{readiness.reorder_defaults_configured ? "Sì" : "No"}</b> <div className="text-xs text-neutral-500">default globali configurati</div></div>
              <div><b>{readiness.categories_with_override}</b> <div className="text-xs text-neutral-500">categorie con override</div></div>
              <div className="col-span-2"><b>{readiness.last_inventory_count_at ? new Date(readiness.last_inventory_count_at).toLocaleDateString("it-IT") : "—"}</b> <div className="text-xs text-neutral-500">ultimo inventario fisico confermato</div></div>
            </div>
          </div>

          {/* Global defaults */}
          <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white" data-testid="globals-panel">
            <div className="label-eyebrow mb-3">Default globali</div>
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <Label>Safety days (globale)</Label>
                <Input type="number" min="0" placeholder={`hardcoded: ${settings.defaults.hardcoded_defaults.safety_days}`}
                       value={globals.safety_days}
                       onChange={(e) => setGlobals({ ...globals, safety_days: e.target.value })}
                       data-testid="global-safety" />
              </div>
              <div>
                <Label>Target days (globale)</Label>
                <Input type="number" min="0" placeholder={`hardcoded: ${settings.defaults.hardcoded_defaults.target_days}`}
                       value={globals.target_days}
                       onChange={(e) => setGlobals({ ...globals, target_days: e.target.value })}
                       data-testid="global-target" />
              </div>
              <Button onClick={saveGlobals} disabled={saving} className="bg-neutral-900 text-[#FDFBF7]" data-testid="save-globals">
                Salva globali
              </Button>
            </div>
            <div className="mt-2 text-xs text-neutral-500">
              Effettivo: safety <b>{settings.defaults.effective_safety_days}</b>g · target <b>{settings.defaults.effective_target_days}</b>g
              {globals.safety_days !== "" && globals.target_days !== "" && (
                <> · usato quando prodotto e categoria non hanno override.</>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="border border-[#E2D9C8] rounded-lg p-4 bg-[#F3EDE1]" data-testid="preview-panel">
            <div className="label-eyebrow mb-2">Preview formula (solo educativa)</div>
            <div className="text-sm space-y-1 font-mono">
              <div>Consumo medio: {preview.avg} {preview.unit}/giorno (esempio)</div>
              <div>Lead time: {preview.lead}g (esempio) · Safety: {preview.safety}g · Target: {preview.target}g</div>
              <div>→ Reorder point = {preview.avg} × ({preview.lead} + {preview.safety}) = <b>{preview.reorder_point}</b> {preview.unit}</div>
              <div>→ Target stock = {preview.avg} × {preview.target} = <b>{preview.target_stock}</b> {preview.unit}</div>
            </div>
          </div>

          {/* Categories */}
          <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white" data-testid="categories-panel">
            <div className="label-eyebrow mb-3">Override per categoria</div>
            <div className="text-xs text-neutral-500 mb-2">Gerarchia: prodotto → categoria → default globale. Lasciare vuoto per ereditare.</div>
            <table className="w-full text-sm">
              <thead><tr className="text-left label-eyebrow">
                <th className="p-2">Categoria</th>
                <th className="p-2 text-right">Safety</th>
                <th className="p-2 text-right">Target</th>
                <th className="p-2">Origine</th>
                <th className="p-2"></th>
              </tr></thead>
              <tbody>
                {settings.categories.map((c) => {
                  const inp = catInputs[c.category] || { safety: "", target: "" };
                  return (
                    <tr key={c.category} className="border-t border-[#E2D9C8]" data-testid={`cat-row-${c.category}`}>
                      <td className="p-2">{c.category}</td>
                      <td className="p-2 text-right">
                        <Input type="number" min="0" className="w-24 inline-block" value={inp.safety}
                               placeholder={`${c.effective_safety_days}`}
                               onChange={(e) => setCatInputs({ ...catInputs, [c.category]: { ...inp, safety: e.target.value } })} />
                      </td>
                      <td className="p-2 text-right">
                        <Input type="number" min="0" className="w-24 inline-block" value={inp.target}
                               placeholder={`${c.effective_target_days}`}
                               onChange={(e) => setCatInputs({ ...catInputs, [c.category]: { ...inp, target: e.target.value } })} />
                      </td>
                      <td className="p-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-neutral-100">
                          safety: {c.safety_source} · target: {c.target_source}
                        </span>
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => saveCategory(c.category)} data-testid={`save-cat-${c.category}`}>Salva</Button>
                        {(c.safety_source === "category" || c.target_source === "category") && (
                          <Button size="sm" variant="ghost" onClick={() => resetCategory(c.category)} data-testid={`reset-cat-${c.category}`}>Ripristina eredita</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
